import { useOrganization, useUser } from "@clerk/expo";
import {
  Archive,
  FolderInput,
  Pencil,
  RotateCcw,
  Trash2,
  UserPlus,
} from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import FolderFilled from "@/components/icons/FolderFilled";
import FolderShared from "@/components/icons/FolderShared";
import { ShareModal } from "@/components/studies";
import {
  ActionMenu,
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  Button,
  ButtonText,
  Card,
  DeleteDialog,
  Heading,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Folder } from "@/types/resources";
import MoveToModal from "./MoveToModal";

interface FolderCardProps {
  className?: string;
  compact?: boolean;
  folder: Folder;
  onDeleted?: () => void;
  onMoved?: () => void;
  onPress: () => void;
  onRenamed?: (folder: Folder) => void;
}

const FolderCard: FC<FolderCardProps> = ({
  className,
  compact = false,
  folder,
  onDeleted,
  onMoved,
  onPress,
  onRenamed,
}) => {
  const { user } = useUser();
  const { membership, organization } = useOrganization();
  const { i18n } = useLocalization();
  const isShared = folder.is_shared;

  const isAdmin = user?.publicMetadata?.admin === true;
  const canDelete = isAdmin || membership?.role === "org:admin";
  const isArchived = !!folder.archived_at;

  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameName, setRenameName] = useState(folder.name);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    error: renameError,
    loading: renaming,
    updateFolder,
  } = API.Folders.useUpdateFolder();
  const {
    archiveFolder,
    error: archiveError,
    loading: archiving,
  } = API.Folders.useArchiveFolder();
  const {
    deleteFolder,
    error: deleteError,
    loading: deletingFolder,
  } = API.Folders.useDeleteFolder();

  // Seed the rename field from the folder name during render (not in an
  // effect) whenever the dialog opens or the folder name changes.
  const renameSyncKey = `${showRenameDialog}|${folder.name}`;
  const [prevRenameSyncKey, setPrevRenameSyncKey] = useState(renameSyncKey);
  if (prevRenameSyncKey !== renameSyncKey) {
    setPrevRenameSyncKey(renameSyncKey);
    if (showRenameDialog) setRenameName(folder.name);
  }

  const handleRenameConfirm = async () => {
    if (!renameName.trim()) return;
    const result = await updateFolder(folder.id, {
      name: renameName.trim(),
      parent_id: folder.parent,
    });
    if (result) {
      setShowRenameDialog(false);
      onRenamed?.(result);
    }
  };

  const subtitleText =
    folder.children_count > 0
      ? i18n.t("folders.subFolderCount", { count: folder.children_count })
      : null;

  const canEdit = folder.can_edit;

  const menuActions = useMemo(() => {
    if (isArchived) {
      const actions = [
        ...(canEdit
          ? [
              {
                color: "typography" as const,
                icon: RotateCcw,
                label: i18n.t("misc.restore"),
                onPress: () => setShowRestoreDialog(true),
              },
            ]
          : []),
        ...(canDelete
          ? [
              {
                color: "error" as const,
                divider: true as const,
                icon: Trash2,
                label: i18n.t("misc.deletePermanently"),
                onPress: () => setShowDeleteDialog(true),
              },
            ]
          : []),
      ];
      return actions.length > 0 ? actions : null;
    }

    return [
      {
        color: "typography" as const,
        icon: Pencil,
        label: i18n.t("folders.renameFolder"),
        onPress: () => setShowRenameDialog(true),
      },
      ...(organization
        ? [
            {
              color: "typography" as const,
              divider: true as const,
              icon: UserPlus,
              label: i18n.t("sharing.share"),
              onPress: () => setShowShareModal(true),
            },
          ]
        : []),
      {
        color: "typography" as const,
        divider: !organization ? (true as const) : undefined,
        icon: FolderInput,
        label: i18n.t("misc.move"),
        onPress: () => setShowMoveModal(true),
      },
      {
        color: "error" as const,
        divider: true as const,
        icon: Archive,
        label: i18n.t("misc.archive"),
        onPress: () => setShowArchiveDialog(true),
      },
    ];
  }, [canDelete, canEdit, i18n, isArchived, organization]);

  return (
    <>
      {compact ? (
        <Box className={className}>
          <Pressable onPress={onPress}>
            <Box className="w-[260px] h-12 rounded-xl border border-outline-200 bg-background-0 px-3 hover:border-outline-300 hover:shadow-soft-1 transition-all duration-200">
              <HStack className="h-full items-center gap-2">
                {isShared ? (
                  <FolderShared
                    color="rgb(var(--color-typography-400))"
                    size={24}
                  />
                ) : (
                  <FolderFilled
                    color="rgb(var(--color-typography-400))"
                    size={22}
                  />
                )}
                <Box className="flex-1 min-w-0">
                  <Text
                    className="text-sm font-medium text-typography-800"
                    isTruncated
                  >
                    {folder.name}
                  </Text>
                </Box>
                {menuActions && (canEdit || isArchived) && (
                  <ActionMenu actions={menuActions} />
                )}
              </HStack>
            </Box>
          </Pressable>
        </Box>
      ) : (
        <Box className={`h-full ${className || ""}`}>
          <Pressable className="h-full" onPress={onPress}>
            <Card className="relative border border-outline-200 rounded-2xl p-5 h-full hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-300">
              <Box className="absolute top-3 right-3 z-10">
                {menuActions && (canEdit || isArchived) && (
                  <ActionMenu actions={menuActions} />
                )}
              </Box>
              <VStack className="gap-3">
                <Box
                  className="w-10 h-10 rounded-xl items-center justify-center relative"
                  style={{
                    backgroundColor: "rgb(var(--color-primary-50))",
                  }}
                >
                  {isShared ? (
                    <FolderShared
                      color="rgb(var(--color-primary-500))"
                      size={24}
                    />
                  ) : (
                    <FolderFilled
                      color="rgb(var(--color-primary-500))"
                      size={22}
                    />
                  )}
                </Box>
                <VStack className="gap-0.5">
                  <Text
                    className="text-sm font-semibold text-typography-900 line-clamp-2"
                    numberOfLines={2}
                  >
                    {folder.name}
                  </Text>
                  {subtitleText && (
                    <Text className="text-xs text-typography-500">
                      {subtitleText}
                    </Text>
                  )}
                </VStack>
              </VStack>
            </Card>
          </Pressable>
        </Box>
      )}

      <AlertDialog
        isOpen={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
      >
        <AlertDialogBackdrop />
        <AlertDialogContent className="gap-6">
          <AlertDialogHeader>
            <Heading size="lg">{i18n.t("folders.rename")}</Heading>
          </AlertDialogHeader>
          <AlertDialogBody>
            <Input className="border border-outline-200 rounded-xl bg-background-50">
              <InputField
                autoFocus
                defaultValue={renameName}
                onChangeText={setRenameName}
                onSubmitEditing={handleRenameConfirm}
                placeholder={i18n.t("folders.folderNamePlaceholder")}
                returnKeyType="done"
              />
            </Input>
          </AlertDialogBody>
          <AlertDialogFooter>
            <VStack className="gap-2 w-full">
              <HStack className="gap-3 justify-end">
                <Button
                  className="rounded-xl"
                  isDisabled={renaming}
                  onPress={() => setShowRenameDialog(false)}
                  variant="outline"
                >
                  <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
                </Button>
                <Button
                  className="rounded-xl"
                  isDisabled={renaming || !renameName.trim()}
                  onPress={handleRenameConfirm}
                >
                  <ButtonText>
                    {renaming
                      ? i18n.t("misc.saving")
                      : i18n.t("folders.renameFolder")}
                  </ButtonText>
                </Button>
              </HStack>
              {renameError && (
                <Text className="text-error-600 text-sm text-right">
                  {renameError}
                </Text>
              )}
            </VStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showMoveModal && (
        <MoveToModal
          currentFolderId={folder.parent}
          excludeSelfId={folder.id}
          isOpen={true}
          itemName={folder.name}
          onClose={() => setShowMoveModal(false)}
          onMove={async (targetFolderId) => {
            const result = await updateFolder(folder.id, {
              name: folder.name,
              parent_id: targetFolderId,
            });
            if (result) onMoved?.();
            return !!result;
          }}
          type={folder.type}
        />
      )}

      <DeleteDialog
        body={i18n.t("folders.confirmArchiveMessage", { name: folder.name })}
        deleteButtonText={i18n.t("misc.archive")}
        error={archiveError}
        isOpen={showArchiveDialog}
        loading={archiving}
        loadingText={i18n.t("misc.archiving")}
        onCancel={() => setShowArchiveDialog(false)}
        onDelete={async () => {
          const result = await archiveFolder(folder.id, true);
          if (result) {
            setShowArchiveDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("folders.confirmArchive")}
      />

      <DeleteDialog
        body={i18n.t("folders.confirmRestoreMessage")}
        deleteButtonText={i18n.t("misc.restore")}
        error={archiveError}
        isOpen={showRestoreDialog}
        loading={archiving}
        loadingText={i18n.t("misc.restoring")}
        onCancel={() => setShowRestoreDialog(false)}
        onDelete={async () => {
          const result = await archiveFolder(folder.id, false);
          if (result) {
            setShowRestoreDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("misc.restore")}
      />

      <DeleteDialog
        body={i18n.t("folders.confirmDeletePermanentlyMessage")}
        deleteButtonText={i18n.t("misc.deletePermanently")}
        error={deleteError}
        isOpen={showDeleteDialog}
        loading={deletingFolder}
        loadingText={i18n.t("misc.deleting")}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          const result = await deleteFolder(folder.id);
          if (result) {
            setShowDeleteDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("folders.deleteFolderPermanently")}
      />

      {showShareModal && (
        <ShareModal
          isOpen
          onClose={() => setShowShareModal(false)}
          ownerId={folder.created_by_id}
          resourceId={folder.id}
          resourceName={folder.name}
          resourceType="folder"
        />
      )}
    </>
  );
};

export default FolderCard;
